'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  DisabledInput,
  SubmitButtonWithValidation,
  TextInput,
} from '@pioz/mlm-skeleton/form-fields';
import { ClipboardCopy } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { EnableTwoFaSchema, enableTwoFaSchema } from './enable-two-fa-schema';
import { CommonLocals } from '@pioz/mlm-common';
import Form from 'next/form';

export function ActivateTwoFaForm({
  locale,
  secret,
  email,
  activateAction,
  titleClass,
  t,
  errorsT,
}: {
  locale: CommonLocals;
  secret: string;
  email: string;
  activateAction: (data: FormData) => Promise<void>;
  titleClass?: string;
  t: {
    title: string;
    secret: string;
    pin: string;
    submit: string;
    enabled: string;
    enableError: string;
    copied: string;
  };
  errorsT: {
    required: string;
  };
}) {
  const {
    register,
    reset,
    trigger,
    formState: { errors, isValid, isDirty },
  } = useForm<EnableTwoFaSchema>({
    defaultValues: {
      secret,
      pin: '',
    },
    mode: 'all',
    resolver: zodResolver(enableTwoFaSchema(errorsT)),
  });

  return (
    <div className="grid h-full grid-rows-[auto_auto_1fr] gap-4">
      <h2 className={titleClass}>{t.title}</h2>
      <QRCodeCanvas
        className="mx-auto"
        id="qr-code"
        value={`otpauth://totp/${email}?secret=${secret}&issuer=${process.env.NEXT_PUBLIC_APP_NAME}`}
      />
      <Form
        action={async (data) => {
          try {
            await activateAction(data);
            toast.success(t.enabled);
          } catch (e) {
            toast.error(t.enableError);
          } finally {
            reset();
          }
        }}
        className="flex flex-col justify-between gap-3"
      >
        <div>
          <input type="hidden" {...register('secret')} />
          <div className="join w-full">
            <DisabledInput value={secret} inputClass="join-item" className="w-full" />
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(secret);
                toast.success(t.copied);
              }}
              className="btn btn-primary join-item my-1!"
            >
              <ClipboardCopy />
            </button>
          </div>
          <TextInput required {...register('pin')} label={t.pin} error={errors.pin} />
        </div>
        <SubmitButtonWithValidation
          locale={locale}
          isValid={isValid}
          isDirty={isDirty}
          trigger={trigger}
          className="btn btn-primary self-end"
        >
          {t.submit}
        </SubmitButtonWithValidation>
      </Form>
    </div>
  );
}
